/**
 * First Session Free (client). Status comes from /api/promo/first-free/status;
 * the server decides everything (switch, cap, eligibility). Talkers never see
 * counts: copy says "while spots last".
 */
import { useEffect, useState } from 'react';
import { fetchFirstFreeStatus } from './api';
import { readBrowserSharetribeToken } from './sharetribeToken';
import appSettings from '../config/settings';

const TTL_MS = 60 * 1000;
let cache = { key: null, at: 0, data: null, promise: null };
const listeners = new Set();

export const FREE_LINE_ITEM_CODE = 'line-item/first-session-free';
export const FREE_FORMATS = ['chat', 'voice', 'video'];
export const FREE_PRICES = { chat: '$10', voice: '$20', video: '$30' };
export const FORMAT_VALUE_SUBUNITS = { chat: 1000, voice: 2000, video: 3000 };
export const FORMAT_WORDS = { chat: 'chat', voice: 'voice', video: 'video' };
/** Earned reward from status: { format, priceSubunits } or null. */
export const rewardOf = status => (status?.role === 'talker' && status?.talker?.reward) || null;
/** Free only up to the value of the paid session they completed. */
export const formatWithinReward = (format, reward) =>
  !!reward && Number.isFinite(FORMAT_VALUE_SUBUNITS[format]) &&
  FORMAT_VALUE_SUBUNITS[format] <= reward.priceSubunits;

const profileOf = user => user?.attributes?.profile || {};

export const isUserVerified = user => {
  const p = profileOf(user);
  const pub = p.publicData || {};
  const prot = p.protectedData || {};
  return !!((pub.ageVerified || prot.ageVerified) && (pub.identityVerified || prot.identityVerified));
};

export const statusKeyFor = user =>
  user?.id?.uuid ? `${user.id.uuid}:${isUserVerified(user) ? 'v' : 'u'}` : 'anon';

const emit = () => listeners.forEach(fn => fn(cache.data));

export const loadFirstFreeStatus = ({ key = 'anon', force = false } = {}) => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return Promise.resolve(null);
  }
  const fresh = cache.key === key && Date.now() - cache.at < TTL_MS;
  if (!force && fresh && cache.data) {
    return Promise.resolve(cache.data);
  }
  if (!force && cache.key === key && cache.promise) {
    return cache.promise;
  }
  cache.key = key;
  cache.promise = Promise.resolve()
    .then(() => fetchFirstFreeStatus(null))
    .then(res => {
      cache = { key, at: Date.now(), data: res?.data || null, promise: null };
      emit();
      return cache.data;
    })
    .catch(() => {
      cache = { key, at: Date.now(), data: null, promise: null };
      emit();
      return null;
    });
  return cache.promise;
};

export const invalidateFirstFreeStatus = () => {
  cache = { key: null, at: 0, data: null, promise: null };
};

/**
 * @param {Object|null} currentUser
 * @returns {{ offer?: {live:boolean,nextMonth:string}, role?: string, talker?: Object, listener?: Object }|null}
 */
export const useFirstFreeStatus = currentUser => {
  const key = statusKeyFor(currentUser);
  const [data, setData] = useState(() => (cache.key === key ? cache.data : null));
  useEffect(() => {
    let live = true;
    const onChange = next => {
      if (live && cache.key === key) setData(next);
    };
    listeners.add(onChange);
    loadFirstFreeStatus({ key }).then(next => {
      if (live) setData(next);
    });
    return () => {
      live = false;
      listeners.delete(onChange);
    };
  }, [key]);
  return data;
};

export const offerLive = status => !!status?.offer?.live;
export const talkerEligible = status => !!(status?.offer?.live && status?.talker?.eligible);
export const talkerNeedsVerify = status =>
  !!(status?.offer?.live && status?.role === 'talker' && ['not_verified', 'email_not_verified'].includes(status?.talker?.reason));
export const listenerPromoLive = status =>
  !!(status?.offer?.live && (status?.role === 'listener' || !!status?.listener));

/** Free tx check that works on speculated or real transactions. */
export const isFirstSessionFreeTx = tx => {
  const pd = tx?.attributes?.protectedData || {};
  const md = tx?.attributes?.metadata || {};
  if (pd.firstSessionFree === true || pd.promo === 'first_free' || md.promo === 'first_free') {
    return true;
  }
  return (tx?.attributes?.lineItems || []).some(li => li?.code === FREE_LINE_ITEM_CODE && !li?.reversal);
};

/** Sweet Treat booking: free first session the listener gives unpaid (Oct 19 2026). */
export const isSweetTreatTx = tx => {
  if (!isFirstSessionFreeTx(tx)) return false;
  const pd = tx?.attributes?.protectedData || {};
  const md = tx?.attributes?.metadata || {};
  return pd.sweetTreat === true || pd.listenerFunded === true || md.sweetTreat === true;
};

export const SWEET_TREAT_LINE =
  'Your first session is free, any format, with listeners and teachers showing the Sweet Treat badge.';
export const SWEET_TREAT_BADGE = 'Sweet Treat: free first session';
export const SWEET_TREAT_LISTENER_LABEL = 'Sweet Treat · Free session (unpaid)';
export const SWEET_TREAT_TALKER_LABEL = 'Free (Sweet Treat)';

export const listenerPayoutLabelOf = tx => {
  const pd = tx?.attributes?.protectedData || {};
  if (Number.isFinite(pd.listenerPayoutSubunits)) {
    return `$${(pd.listenerPayoutSubunits / 100).toFixed(2)}`;
  }
  return pd.listenerPayoutLabel || null;
};

// ------------------------------------------------------- login pop-up gate --

const POPUP_KEY = 'sl.firstFreePopup.v1';

const hash = str => {
  let h = 5381;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
};

/** One value per login (the refresh token stays the same for a whole login). */
export const loginSessionKey = userId => {
  let token = null;
  try {
    token = readBrowserSharetribeToken({ clientId: appSettings.sdk.clientId });
  } catch (e) {
    token = null;
  }
  const refresh = token?.refresh_token || token?.refreshToken || null;
  return refresh ? `${userId}:${hash(refresh)}` : null;
};

export const popupAlreadyShown = userId => {
  if (typeof window === 'undefined') return true;
  try {
    const loginKey = loginSessionKey(userId);
    if (loginKey) {
      return window.localStorage.getItem(POPUP_KEY) === loginKey;
    }
    return window.sessionStorage.getItem(POPUP_KEY) === String(userId);
  } catch (e) {
    return false;
  }
};

export const markPopupShown = userId => {
  if (typeof window === 'undefined') return;
  try {
    const loginKey = loginSessionKey(userId);
    if (loginKey) {
      window.localStorage.setItem(POPUP_KEY, loginKey);
    }
    window.sessionStorage.setItem(POPUP_KEY, String(userId));
  } catch (e) {
    // Private mode: the pop-up may show again next page load in this tab only.
  }
};

// The booking panel showed "Free" for this listing: checkout asks the server to
// refuse (409) rather than silently quote the normal price if free isn't allowed.
const EXPECT_KEY = 'sl_ff_expect_';
export const rememberFreeOffer = (listingId, on, maxSubunits = 0) => {
  if (typeof window === 'undefined' || !listingId) return;
  try {
    if (on) {
      window.sessionStorage.setItem(EXPECT_KEY + listingId, String(maxSubunits || 0));
    } else {
      window.sessionStorage.removeItem(EXPECT_KEY + listingId);
    }
  } catch (e) {
    // storage blocked: the server still re-checks every free request
  }
};
export const expectsFreeFor = (listingId, format) => {
  if (typeof window === 'undefined' || !listingId) return false;
  try {
    const max = Number(window.sessionStorage.getItem(EXPECT_KEY + listingId));
    return max > 0 && FORMAT_VALUE_SUBUNITS[format] <= max;
  } catch (e) {
    return false;
  }
};
